import { commonComponents as cc } from "@/components/common";
import { ui } from "@/components/ui";
import { ClipboardCheck, Info } from "lucide-react";

import type { ToT } from "@/route-types";
import type { FormViewT, InputT, PageViewT } from "@/types/database/forms";
import type { DueEntryT } from "@/types/database/trackers";
import { useEffect } from "react";
import type { UseFormReturn } from "react-hook-form";
import type { SubmitHandler } from "react-hook-form";
import Call from "../../../components/common/Call";
import { useSelectFormById } from "../../forms/api/selectFormById";
import InputSwitch from "../../inputs/components/InputSwitch";
import { stepperizeDiaryPages } from "../../inputs/services/stepperizeDiaryPages";

interface Props {
	route: ToT;
	pageIndex: number;
	form: FormViewT;
	nextEntry?: DueEntryT | null;
	diaryId?: string;
	formType: "observation" | "tracker";
	// Form control props
	reactHookForm: UseFormReturn<Record<string, unknown>>;
	onSubmit: SubmitHandler<Record<string, unknown>>;
	prevPage: () => void;
	nextPage?: () => void;
}

// Define an interface for inputs with input_index
// We're using a type assertion approach instead of extending InputT
type InputWithIndex = InputT & {
	input_index?: number;
};

export type AnyInputT = string | string[] | number | number;

// Debug component to show validation errors
const FormDebug = ({
	form,
}: { form: UseFormReturn<Record<string, unknown>> }) => {
	// Only show in development
	if (process.env.NODE_ENV !== "development") return null;

	const hasErrors = Object.keys(form.formState.errors).length > 0;

	if (!hasErrors) return null;

	return (
		<ui.Card className="bg-red-50 mb-4 border-red-300">
			<ui.CardHeader>
				<ui.CardTitle className="text-red-600">
					Form Validation Errors
				</ui.CardTitle>
			</ui.CardHeader>
			<ui.CardContent>
				<pre className="bg-white p-2 border rounded max-h-36 overflow-auto text-xs">
					{JSON.stringify(form.formState.errors, null, 2)}
				</pre>
				<div className="mt-2 text-sm">
					<p className="font-semibold">Current Form Values:</p>
					<pre className="bg-white mt-1 p-2 border rounded max-h-36 overflow-auto text-xs">
						{JSON.stringify(form.getValues(), null, 2)}
					</pre>
				</div>
			</ui.CardContent>
		</ui.Card>
	);
};

const FormPage = (props: Props) => {
	const { data: form } = useSelectFormById(props.form.form_id);

	const page = props.form?.pages?.find(
		(p: PageViewT) => p?.page_index === props.pageIndex,
	) as PageViewT;

	if (Number(props.pageIndex) > form?.pages?.length) {
		return (
			<div className="flex flex-col gap-4 bg-green-200 mx-auto p-2 px-8 py-4 rounded-lg w-fit text-green-800">
				<div className="flex items-center gap-4 mx-auto">
					<ClipboardCheck size={48} strokeWidth={1.5} />
					<p className="font-medium text-lg">Form Submitted</p>
				</div>

				<ui.Button variant="outline">Next</ui.Button>
			</div>
		);
	}

	useEffect(() => {
		window.scrollTo({
			top: document.getElementById("form-top")?.offsetHeight,
			behavior: "smooth",
		});
	}, [props.pageIndex]);

	const steps = stepperizeDiaryPages(
		form?.pages,
		props.pageIndex,
		props.route.to,
	);

	return (
		<>
			<cc.Title
				words={form?.form_name ?? "Form"}
				description={form?.form_description ?? undefined}
			/>

			{steps?.length && steps?.length > 1 && (
				<>
					<cc.Stepper steps={steps} />
					<ui.Separator />
					<br id="form-top" />
				</>
			)}
			{form?.form_instructions && (
				<Call icon={<Info size={20} />} words={form?.form_instructions} />
			)}

			{/* Debug information */}
			<FormDebug form={props.reactHookForm} />

			<ui.Form {...props.reactHookForm}>
				<form
					onSubmit={props.reactHookForm?.handleSubmit(props.onSubmit)}
					className="space-y-4 mt-6 p-0 text-lg"
				>
					{page?.inputs
						?.sort((a, b) => {
							// Use type assertion with unknown as an intermediate step
							const aInput = a as unknown as InputWithIndex;
							const bInput = b as unknown as InputWithIndex;
							return (
								Number(aInput?.input_index || 0) -
								Number(bInput?.input_index || 0)
							);
						})
						?.map((i) => (
							<div className="mb-4" key={i?.input_id}>
								<InputSwitch form={props.reactHookForm} input={i} />
							</div>
						))}
					<cc.FooterButtons
						words1="Next"
						primaryButtonType="next"
						words2={form?.pages?.length > 1 ? "Back" : undefined}
						func2={form?.pages?.length > 1 ? props.prevPage : undefined}
						disabled2={props.pageIndex === 1}
					/>
				</form>
			</ui.Form>
		</>
	);
};

export default FormPage;
